import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import ReCAPTCHA from "react-google-recaptcha";

import Footer from "../components/Footer";

const CHANNELS = [
  {
    href: "mailto:info@bleep.ug",
    icon: "fas fa-envelope",
    label: "Email",
    value: "info@bleep.ug",
  },
  {
    href: "https://wa.me/256746502009",
    icon: "fab fa-whatsapp",
    label: "WhatsApp",
    value: "+256 746 502009",
    external: true,
  },
  {
    href: "tel:+256760347013",
    icon: "fas fa-phone",
    label: "Direct line",
    value: "+256 760 347013",
  },
  {
    href: "https://bleep.ug",
    icon: "fas fa-globe",
    label: "Website",
    value: "bleep.ug",
    external: true,
  },
];

const SOCIALS = [
  { href: "https://x.com/bleep_app_", icon: "fab fa-x-twitter", label: "X" },
  {
    href: "https://www.instagram.com/bleep_ug",
    icon: "fab fa-instagram",
    label: "Instagram",
  },
  {
    href: "https://www.linkedin.com/company/bleepapp/posts/?feedView=all",
    icon: "fab fa-linkedin-in",
    label: "LinkedIn",
  },
];

const Contact = ({ domain }) => {
  const recaptchaRef = useRef(null);
  const alertRef = useRef(null);

  const [captchaToken, setCaptchaToken] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState({ type: "", message: "" });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!captchaToken) {
      setAlert({ type: "danger", message: "Please complete the reCAPTCHA." });
      return;
    }

    setLoading(true);
    setAlert({ type: "", message: "" });

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, captchaToken }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Submission failed.");

      setAlert({
        type: "success",
        message:
          "Thanks for getting in touch. Your message was sent, and we will reply using the contact details you gave us.",
      });

      setFormData({ name: "", email: "", phone: "", message: "" });
      recaptchaRef.current?.reset();
      setCaptchaToken(null);
    } catch (err) {
      console.error(err);
      setAlert({
        type: "danger",
        message: "Something went wrong: " + err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (alert.message && alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [alert.message]);

  return (
    <div className="bleep-light bl-page">
      <Helmet>
        <title>Contact | BLEEP</title>
        <meta
          name="description"
          content="Get in touch with the BLEEP team by message, email, WhatsApp or phone."
        />
      </Helmet>

      <section className="bl-section">
        <div className="bl-shell">
          <div className="bl-feed-head">
            <h1 className="bl-page-title">Contact us</h1>
            <p className="bl-page-sub">
              Questions, feedback, or a report you want followed up. We reply
              within one to two business days.
            </p>
          </div>

          <div className="contact-layout">
            {/* Message form */}
            <div className="contact-card">
              {alert.message && (
                <div
                  ref={alertRef}
                  className={`bl-alert ${alert.type === "success" ? "bl-alert--ok" : "bl-alert--danger"}`}
                  role="alert"
                  aria-live="polite"
                >
                  <i
                    className={`bl-alert__icon fas ${
                      alert.type === "success"
                        ? "fa-circle-check"
                        : "fa-circle-exclamation"
                    }`}
                    aria-hidden="true"
                  />
                  <div>{alert.message}</div>
                  <button
                    type="button"
                    className="bl-alert__close"
                    aria-label="Dismiss"
                    onClick={() => setAlert({ type: "", message: "" })}
                  >
                    <i
                      className="fas fa-xmark"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              )}

              <h2 className="contact-card__title">Send us a message</h2>
              <p className="contact-card__sub">
                Tell us what you need and how to reach you.
              </p>

              <form
                onSubmit={handleSubmit}
                noValidate
              >
                <div className="bl-form-stack">
                  <div className="bl-field">
                    <label
                      className="bl-field__label"
                      htmlFor="name"
                    >
                      Your name
                    </label>
                    <input
                      className="bl-input"
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      aria-required="true"
                      autoComplete="name"
                    />
                  </div>

                  <div className="bl-form-row">
                    <div className="bl-field">
                      <label
                        className="bl-field__label"
                        htmlFor="email"
                      >
                        Email
                      </label>
                      <input
                        className="bl-input"
                        type="email"
                        id="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        aria-required="true"
                        inputMode="email"
                        autoComplete="email"
                      />
                    </div>

                    <div className="bl-field">
                      <label
                        className="bl-field__label"
                        htmlFor="phone"
                      >
                        Phone <em>(optional)</em>
                      </label>
                      <input
                        className="bl-input"
                        type="tel"
                        id="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        inputMode="tel"
                        pattern="^[0-9+\-\s()]{6,}$"
                        autoComplete="tel"
                      />
                    </div>
                  </div>

                  <div className="bl-field">
                    <label
                      className="bl-field__label"
                      htmlFor="message"
                    >
                      Message
                    </label>
                    <textarea
                      className="bl-textarea"
                      id="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      aria-required="true"
                    />
                    <span className="bl-field__hint">
                      We will never share your email or phone number with anyone
                      else.
                    </span>
                  </div>

                  <ReCAPTCHA
                    sitekey="6LdzNZ0rAAAAAN575rBQ0e4fPF211CDYeo4W6rS0"
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    ref={recaptchaRef}
                  />
                </div>

                <div className="contact-form-foot">
                  {/* The small variant of the primary CTA: it swaps papaya for
                      the deeper brand-ink step, which is the only one that
                      holds 4.5:1 under a 14px label. */}
                  <button
                    type="submit"
                    className="bleep-btn-report bleep-btn-report--sm"
                    disabled={loading}
                    aria-busy={loading ? "true" : "false"}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Sending
                      </>
                    ) : (
                      <>
                        Send message
                        <i
                          className="fas fa-paper-plane"
                          aria-hidden="true"
                        />
                      </>
                    )}
                  </button>
                  <span className="contact-form-foot__note">
                    Usually answered within one to two business days.
                  </span>
                </div>
              </form>
            </div>

            {/* Direct channels */}
            <aside className="contact-card">
              <h2 className="contact-card__title">Reach us directly</h2>
              <p className="contact-card__sub">
                Skip the form and use whichever suits you.
              </p>

              <div className="contact-channels">
                {CHANNELS.map(({ href, icon, label, value, external }) => (
                  <a
                    key={href}
                    href={href}
                    className="contact-channel"
                    {...(external
                      ? { target: "_blank", rel: "noreferrer" }
                      : null)}
                  >
                    <span className="contact-channel__icon">
                      <i
                        className={icon}
                        aria-hidden="true"
                      />
                    </span>
                    <span>
                      <span className="contact-channel__label">{label}</span>
                      <span className="contact-channel__value">{value}</span>
                    </span>
                  </a>
                ))}
              </div>

              <div className="contact-group-label">Follow us</div>
              <div className="contact-socials">
                {SOCIALS.map(({ href, icon, label }) => (
                  <a
                    key={href}
                    href={href}
                    className="contact-social"
                    aria-label={`BLEEP on ${label}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <i
                      className={icon}
                      aria-hidden="true"
                    />
                  </a>
                ))}
              </div>
            </aside>
          </div>
        </div>
      </section>

      <Footer domain={domain} />
    </div>
  );
};

export default Contact;
